import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';
import AnimatedArcs from '../AnimatedArcs';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';

export interface IVideo {
	src: string;
}
export interface ITabVideoProps {
	url?: IVideo;
	/**
	 * @noUI
	 */
	duration?: number;
}

export interface ITabbedVideoProps {
	/**
	 * @size resize
	 */
	posterImage?: IImage;
	video?: ITabVideoProps;
	videoLabel?: string;
	videoCtaButton?: ILink;
}

export interface IMainHeroTabbedVideoProps extends IBaseElementProps {
	heading?: string;
	shortDescription?: string;
	/**
	 * @size resize
	 */
	backgroundImage?: IImage;
	ctaButton?: ILink;
	formLightboxButton?: IFormLightboxButtonProps;
	tabbedVideos?: ITabbedVideoProps[];
	arcsPosition: 'Left' | 'Right';
}

/**
 * Calian Main Hero - Tabbed Video
 * @icon cover-image
 * @block
 */
const MainHeroTabbedVideo: React.FC<IMainHeroTabbedVideoProps> = ({
	className,
	classNames = [],
	heading,
	shortDescription,
	backgroundImage,
	ctaButton,
	formLightboxButton,
	tabbedVideos,
	arcsPosition = 'Left',
}) => {
	const videoRefs = useRef<HTMLVideoElement[]>([]);
	const [videoPlayingStates, setVideoPlayingStates] = useState<boolean[]>([]);
	const [videoCurrentTimes, setVideoCurrentTimes] = useState<number[]>([]);
	const [currentVideoIndex, setCurrentVideoIndex] = useState<number>(0);

	useEffect(() => {
		setVideoPlayingStates((prevStates) => {
			const vids = tabbedVideos || [];
			if (prevStates.length < vids.length) {
				return [
					...prevStates,
					...new Array(vids.length - prevStates.length).fill(false),
				];
			} else {
				return prevStates.slice(0, vids.length);
			}
		});
		setVideoCurrentTimes((prevTimes) => {
			const vids = tabbedVideos || [];
			if (prevTimes.length < vids.length) {
				return [
					...prevTimes,
					...new Array(vids.length - prevTimes.length).fill(0),
				];
			} else {
				return prevTimes.slice(0, vids.length);
			}
		});
	}, [tabbedVideos]);

	useEffect(() => {
		videoRefs.current = videoRefs.current.slice(
			0,
			tabbedVideos?.length || 0,
		);
	}, [tabbedVideos]);

	const handleVideoPlay = (index: number) => {
		setCurrentVideoIndex(index);
		setVideoPlayingStates((prevStates) =>
			prevStates.map((_, i) => i === index),
		);
	};

	const handleVideoPause = (index: number) => {
		setVideoPlayingStates((prevStates) => {
			const newStates = [...prevStates];
			newStates[index] = false;
			return newStates;
		});
	};

	const handleVideoTimeUpdate = (index: number, currentTime: number) => {
		setVideoCurrentTimes((prevTimes) => {
			const newTimes = [...prevTimes];
			newTimes[index] = currentTime;
			return newTimes;
		});
	};

	const playNextVideo = () => {
		const nextVideoIndex =
			(currentVideoIndex + 1) % (tabbedVideos?.length || 0);
		handleVideoPlay(nextVideoIndex);
	};

	useEffect(() => {
		if (tabbedVideos && tabbedVideos.length > 0) {
			const video = videoRefs.current[currentVideoIndex];
			if (video) {
				video.addEventListener('ended', playNextVideo);
				video.play();
				return () => {
					video.removeEventListener('ended', playNextVideo);
				};
			}
		}
	}, [tabbedVideos, currentVideoIndex]);

	useEffect(() => {
		if (tabbedVideos && tabbedVideos.length > 0) {
			handleVideoPlay(0);
		}
	}, [tabbedVideos]);

	return (
		<div className="calian-main-hero">
			<div
				className={
					className || cn('main-hero-tabbed-video', ...classNames)
				}
			>
				<div
					className="main-hero-tabbed-video__background"
					style={
						backgroundImage && {
							background: `url(${backgroundImage?.src}) #d9d9d9 50% / cover no-repeat`,
						}
					}
				>
					{' '}
					<div className="main-hero-tabbed-video__background-overlay--one"></div>
					<div className="main-hero-tabbed-video__background-overlay--two"></div>
					<AnimatedArcs
						arcsStyle={'Component - Two Lines'}
						arcsPosition={arcsPosition}
						arcOneColor={'var(--bs-primary-mid)'}
						arcOneStrokeWidth={4}
						arcTwoColor={'var(--bs-white)'}
						arcTwoStrokeWidth={3}
						animationDelay={250}
						animationOffset={150}
					/>
				</div>
				<div className="main-hero-tabbed-video__container container-fluid mw-1252px position-relative">
					<div className="row align-items-lg-end mt-lg-11 pt-7 pt-sm-11 pt-md-7 mb-3">
						<div className="col-12 col-lg-6 ps-lg-6">
							{heading && (
								<h1 className="main-hero-tabbed-video__heading text-white ms-1 mt-1 mb-4 pe-5">
									{heading}
								</h1>
							)}
						</div>
						<div className="col-12 col-lg-6">
							{shortDescription && (
								<p className="main-hero-tabbed-video__short-description text-white text-start text-lg-end subheading mb-4 me-1 pb-1 pe-3">
									{shortDescription}
								</p>
							)}
						</div>
					</div>
					<div className="row">
						<div className="main-hero-tabbed-video__video-listing col-12 p-0">
							{tabbedVideos?.map((tabbedVideo, index) => (
								<div
									className={`main-hero-tabbed-video__video-listing-item ${
										currentVideoIndex === index
											? 'active'
											: 'hidden'
									}`}
									key={index}
								>
									{tabbedVideo?.videoCtaButton?.label && (
										<div className="main-hero-tabbed-video__video-listing-item--overlay rounded-lg-5">
											<Button
												className="btn btn-light"
												{...tabbedVideo?.videoCtaButton}
											/>
										</div>
									)}
									<div className="main-hero-tabbed-video__video-listing-item-video">
										<video
											src={tabbedVideo?.video?.url?.src}
											className="main-hero-tabbed-video__video object-fit-cover rounded-lg-5 overflow-hidden"
											poster={
												tabbedVideo?.posterImage?.src
											}
											autoPlay={videoPlayingStates[index]}
											muted
											playsInline
											ref={(el) => {
												if (el) {
													videoRefs.current[index] =
														el;
												}
											}}
											onTimeUpdate={(event) =>
												handleVideoTimeUpdate(
													index,
													(
														event.target as HTMLVideoElement
													).currentTime,
												)
											}
											onLoadedMetadata={() => {
												if (
													tabbedVideo.video
														?.duration === undefined
												) {
													const video = videoRefs
														.current[
														index
													] as HTMLVideoElement;
													const videoDuration =
														video.duration;
													tabbedVideo.video = {
														...tabbedVideo.video,
														duration: videoDuration,
													};
													setVideoCurrentTimes(
														(prevTimes) => {
															const newTimes = [
																...prevTimes,
															];
															newTimes[index] = 0;
															return newTimes;
														},
													);
												}
											}}
										/>
									</div>
								</div>
							))}
						</div>
					</div>
					<div className="main-hero-tabbed-video__btn-listing--row row">
						<div className="main-hero-tabbed-video__btn-listing--container d-flex flex-row">
							<div className="main-hero-tabbed-video__btn-listing col-12 p-0 d-flex flex-row flex-lg-row align-items-center">
								{tabbedVideos?.map((tabbedVideo, index) => (
									<button
										key={index}
										className="main-hero-tabbed-video__btn btn"
										tabIndex={1}
										onClick={() => {
											if (videoPlayingStates[index]) {
												videoRefs.current[
													index
												].pause();
												handleVideoPause(index);
											} else {
												videoRefs.current[index].play();
												handleVideoPlay(index);
											}
										}}
									>
										<>
											<span
												className={`icon ${
													videoPlayingStates[index]
														? 'icon-pause'
														: 'icon-play'
												} fs-8px me-2`}
											>
												<svg
													className="main-hero-tabbed-video__video-listing-item-progress"
													viewBox="0 0 20 20"
													width="20"
													height="20"
												>
													<circle
														className="background-circle"
														cx="10"
														cy="10"
														r="9"
													/>
													<circle
														className="progress-circle"
														cx="10"
														cy="10"
														r="9"
														strokeWidth="2"
														strokeDasharray="56.548"
														strokeDashoffset={
															-(
																(56.548 *
																	(videoCurrentTimes[
																		index
																	] || 0)) /
																(tabbedVideo
																	?.video
																	?.duration ||
																	1)
															)
														}
													/>
												</svg>
											</span>
											{tabbedVideo?.videoLabel && (
												<span className="btn-text text-white fs-6">
													{tabbedVideo?.videoLabel}
												</span>
											)}
										</>
									</button>
								))}
							</div>
							{(ctaButton ||
								formLightboxButton?.formLightboxButton?.label
									?.length > 0) && (
								<div className="main-hero-tabbed-video__cta-button col-12 p-0 d-flex flex-row justify-content-center align-items-center justify-content-lg-start mt-4 mb-3 my-lg-0">
									{ctaButton && (
										<Button
											className="btn btn-sm btn-primary"
											{...ctaButton}
										/>
									)}
									{formLightboxButton?.formLightboxButton
										?.label?.length > 0 && (
										<FormLightboxButton
											{...formLightboxButton}
										/>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MainHeroTabbedVideo;
